import { AuthService } from './authService';

const API_URL = process.env.REACT_APP_API_URL;
const CSRF_TOKEN_HEADER = 'X-CSRF-TOKEN';

const buildHeaders = () => {
	let headers: { [key: string]: any } = {
		'Content-Type': 'application/json'
	};

	const token = AuthService.token;

	if (token) {
		headers = { ...headers, Authorization: 'Bearer ' + token };
	}

	return headers;
};

const addCsrfTokenToHeaders = (headers: { [key: string]: any }, csrfToken: string) => {
	headers = {
		...headers, [CSRF_TOKEN_HEADER]: csrfToken
	};

	return headers;
};

interface RequestConfig {
	info: string,
	init: RequestInit
}

export interface ApiError {
	detail: string;
	status: number;
	title: string;
	traceId: string,
	type: string;
}


class RestServiceSingleton {
	private onUserTokenUpdate = null;
	private csrfToken: string = '';

	init(onUserTokenUpdate) {
		this.onUserTokenUpdate = onUserTokenUpdate;
	};

	async post({ endPoint, data }, getBuffer = false) {
		const url = this.getApiUrl(endPoint);
		const res = await this.makeBodyUrl('POST', url, data);

		if (getBuffer) return await res.arrayBuffer();

		return await res.json();
	};

	async put({ endPoint, data }) {
		const url = this.getApiUrl(endPoint);
		const res =  await this.makeBodyUrl('PUT', url, data);

		return await res.json();
	};

	async get({ endPoint }) {
		const url = this.getApiUrl(endPoint);
		const res =  await this.makeNoBodyUrl('GET', url);

		return await res.json();
	};

	getApiUrl(url: string) {
		return new URL(url, API_URL).href;
	};

	async makeRequest(requestConfig: RequestConfig, retry = false) {
		await AuthService.requestAccessToken();
		const init = {
			...requestConfig.init,
			headers: buildHeaders(),
		};
		init.mode = 'cors';
		init.credentials = 'include';

		var token = this.csrfToken;
		if (requestConfig.init.method === 'POST' || requestConfig.init.method === 'PUT') {
			init.headers = addCsrfTokenToHeaders(init.headers, token);
		}

		try {
			var info = requestConfig.info;
			var response = await fetch(info, init);
			if (!response.ok) {
				if (response.status === 401) {
					if (!retry) {
						await AuthService.requestAccessToken(response);
						return await this.makeRequest(requestConfig, true);
					} else {
						throw response;
					}
				} else {
					throw response;
				}
			} else {
				var isTokenPresent = response.headers.has(CSRF_TOKEN_HEADER);

				if (isTokenPresent) {
					let csrfToken = response.headers.get(CSRF_TOKEN_HEADER);
					this.csrfToken = csrfToken;
				}

				return response;
			}
		} catch (error: any) {
			let errorData: ApiError;

			try {
				errorData = await error.json();
			} catch (e) {
				errorData = {
					status: error.status || 'unknown',
					title: error.name || 'Service error',
					type: error.url || requestConfig.info,
					detail: error.statusText || error.message,
					traceId: undefined
				};
			}

			return Promise.reject(errorData);
		}
	};

	async makeBodyUrl(method: string, url: string, data: any): Promise<Response> {
		return await this.makeRequest({ info: url, init: { body: JSON.stringify(data), method } });
	};

	async makeNoBodyUrl(method: string, url: string) {
		return await this.makeRequest({ info: url, init: { method } });
	};
}

export const RestService = new RestServiceSingleton();
